<script setup lang='ts'>
import EcoFriendlyIcon from '@/components/labels/EcoFriendlyIcon.vue';
import { Heart } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// @ts-expect-error cannot find declaration
import vue3starRatings from 'vue3-star-ratings';

import type { Product } from '@/types/mode-list-products';

import { useFavorites } from '@/composables/useFavorites.js';
import useLocale from '@/composables/useLocale';
import useModeListData from '@/composables/useModeListData.js';
import { vGtmTrack } from '@/directives/v-gtm-track.js';
import { Language } from '@/types/mode-list-locale';
import {
  convertUrlToResizer,
  formatActivityDuration,
  formatDisplayRating,
  formatPrice,
  isEcoFriendly,
} from '@/utils/utils.js';

import TheCardGallery from './TheCardGallery.vue';

const props = withDefaults(
  defineProps<{
    responsiveRow: boolean;
    product: Product;
    showFavorite?: boolean;
    prefixUrl: string | null;
    small?: boolean;
    showCloseButton?: boolean;
    withSlider?: boolean;
    hasLargeViewport?: boolean;
    // TODO: is favorite as props (in product POJO) -> event on click
  }>(),
  {
    showFavorite: true,
    showCloseButton: false,
    withSlider: false,
    hasLargeViewport: false,
  },
);

const emit = defineEmits(['closePopup']);

const { handleAddCrossSellingToFavorite } = useFavorites();

const { t } = useI18n();
const { properties: _, ...rest } = props.product;

const isFavorite = ref<boolean>(false);
const imageSrc = ref<string>(props.product.image ?? 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png');

const { canonicals } = useModeListData();
const externalLink = computed(() => {
  const canonical = canonicals.value[props.product.id];
  if (!canonical) {
    return props.prefixUrl ? `${props.prefixUrl}${props.product.id}` : `${window.location.origin}/youbook_single/${props.product.id}`;
  }
  return canonical;
});
const duration = computed(() => props.product.properties.find(it => it.propertyName === 'nextSessionDate')?.value?.duration ?? null);

const rating = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.rating ?? null);
const displayedRating = ref(formatDisplayRating(rating.value ?? 0));
const starRating = computed(() => (+displayedRating.value / 2).toFixed(1));

const ratingReviewCount = computed(() => props.product.properties.find(it => it.propertyName === 'rating')?.value?.ratingReviewCount ?? null);

// const locality =`${props.product.location?.locality[0].toLocaleUpperCase()}${props.product.location?.locality.substring(1).toLocaleLowerCase()}`
const locality = `${props.product.location?.locality}`;
const priceFrom = formatPrice(props.product.priceFrom, props.product.currency);
const originalPrice = formatPrice(props.product.originalPrice, props.product.currency);
const hasDiscount = computed(() => (originalPrice > priceFrom) && props.product.originalPrice !== null);

const { currentLocale } = useLocale();
const imageLocalization = props.product.importedInLangs.includes(currentLocale.value) ? currentLocale.value : Language.FRENCH;
const imagesToDisplay = computed(() => props.product.images?.filter((it: any) => it.lang === imageLocalization));

function handleImgError() {
  imageSrc.value = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png';
}

function handleFavorite(event: Event) {
  handleAddCrossSellingToFavorite(event, isFavorite);
}

function openDetails(link: string) {
  window.open(link, '_blank');
}

function handleClose(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  emit('closePopup');
}
</script>

<template>
  <section v-gtm-track:card="{ product: rest }" class="card_wrapper" :class="{ 'row': props.responsiveRow, 'small-card': props.small }" :aria-label="t('card.label')">
    <button v-if="showCloseButton" type="button" class="close-button" aria-label="Fermer" @click="handleClose" @keydown.enter="handleClose">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Icon">
          <path id="Vector" d="M14.6666 5.33301L5.33325 14.6663M5.33325 5.33301L14.6666 14.6663" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </g>
      </svg>
    </button>
    <picture>
      <div v-if="isEcoFriendly(product)" class="eco-friendly-badge">
        <EcoFriendlyIcon />
      </div>
      <img v-if="!props.withSlider || (props.withSlider && !props.hasLargeViewport)" :src="convertUrlToResizer(imageSrc)" alt="lieu de l'activité" loading="lazy" @error="handleImgError" @click="openDetails(externalLink)">
      <TheCardGallery v-if="props.withSlider && props.hasLargeViewport" :images="imagesToDisplay" />
      <button v-if="showFavorite" id="card_favorite" type="button" :data-id="props.product.id" class="favorite" :aria-label="t('card.favorite')" @click="handleFavorite" @keydown.enter="handleFavorite">
        <Heart :class="{ checked: isFavorite }" aria-hidden="true" />
      </button>
    </picture>
    <a :href="externalLink" target="_blank" class="infos_wrapper" :aria-label="t('card.infos')">
      <div class="flex_wrapper">
        <p class="small_text">{{ locality }}</p>
      </div>
      <h3 class="title_text_clamp">{{ props.product.name ?? 'No name found.' }}</h3>
      <div v-if="duration" class="flex_wrapper spacing_bottom">
        <p class="small_text">{{ formatActivityDuration(duration) }}</p>
      <!-- <div>&middot;</div> -->
      </div>
      <div class="rating">
        <vue3starRatings v-if="rating" v-model="starRating" :star-size="16" :disabled-click="true" />
        <p v-if="rating" class="small_text">{{ displayedRating }} / 10<span v-if="ratingReviewCount" class="review-count">({{ ratingReviewCount }})</span></p>
      </div>
      <p class="price_tag" :style="[rating ? 'margin-top: none;' : 'margin-top: auto']">
        {{ t('card.from') }}
        <span :style="[hasDiscount ? 'text-decoration: line-through; font-weight: var(--yb-text-light); color:var(--yb-color-black)' : 'color:var(--yb-color-black);']">
          {{ originalPrice }}
        </span>&nbsp;
        <span v-if="hasDiscount" style="color: var(--yb-color-primary)">{{ priceFrom }}</span>
      </p>
    </a>
  </section>
</template>

<style lang="css" scoped>
.card_wrapper {
  --img-ratio: 4/3;

  all: unset;
  display: flex;
  flex-direction: column;
  background-color: var(--yb-color-white);
  border: 1px solid #ccc;
  border-radius: var(--yb-card-radius);
  overflow: hidden;

  &:focus-visible {
    outline: 2px solid var(--yb-outline);
    outline-offset: 2px;
  }

  picture {
    position: relative;
    width: 100%;
    aspect-ratio: var(--img-ratio);
    overflow: clip;
    overflow: hidden;

    @media screen and (width < 768px) {
      height: 100%;
      aspect-ratio: none;
    }
  }

  img {
    width: 100%;
    aspect-ratio: var(--img-ratio);
    object-fit: cover;
    object-position: center;
    cursor: pointer;
  }

  .favorite {
    position: absolute;
    display: flex;
    background-color: transparent;
    border: none;
    padding: 0;
    top: 10px;
    right: 10px;
    z-index: 5;
    transition: scale 0.3s ease-out;

    &:focus-visible {
      outline: 2px solid var(--yb-outline);
    }

    svg {
      stroke: var(--yb-color-white);
      stroke-width: 2px;
      fill: rgba(0, 0, 0, 0.4);
      cursor: pointer;
    }

    svg.checked {
      stroke: var(--yb-color-primary);
      stroke-width: 2px;
      fill: var(--yb-color-primary);
    }

    @media (prefers-reduced-motion: no-preference) {
      &:hover {
        scale: 1.1;
      }
    }
  }

  .eco-friendly-badge {
    position: absolute;
    display: flex;
    background-color: var(--yb-color-ecofriendly);
    padding: 0.5rem;
    top: 0;
    left: 0.5rem;
    border-radius: 0 0 var(--yb-radius-sm) var(--yb-radius-sm);
    z-index: 5;
  }

  .flex_wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .spacing_top {
    margin-top: var(--yb-spacing-sm);
  }

  .spacing_bottom {
    margin-bottom: var(--yb-spacing-xs);
  }

  .infos_wrapper {
    all: unset;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: var(--yb-spacing-xs);
    padding: 0.8rem;
    min-height: 38%;
    cursor: pointer;
  }

  .rating {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-xs);
    color: var(--yb-color-black);
    margin-top: auto;

    p {
      font-weight: var(--yb-text-bold) !important;
    }

    .review-count {
      margin-left: 4px;
      font-weight: var(--yb-text-light);
    }
  }

  .title_text_clamp {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: var(--yb-text-xl);
    font-weight: var(--yb-text-semi-bold);
    color: var(--yb-card-title-color);
    line-height: var(--yb-text-lineheight);
    transition: color 0.3s ease-out;

    @media screen and (width < 768px) {
      font-size: var(--yb-text-base);
    }
  }

  .small_text {
    color: var(--yb-color-black);
    font-size: var(--yb-text-sm);
    line-height: var(--yb-text-lineheight);

    @media screen and (width < 768px) {
      font-size: var(--yb-text-xs);
    }
  }

  .badge_new {
    max-width: fit-content;
    background-color: var(--yb-color-lightgray);
    color: var(--yb-color-black);
    font-size: var(--yb-text-xs);
    font-weight: var(--yb-text-bold);
    padding: var(--yb-spacing-xs);
    border-radius: var(--yb-radius-sm);

    @media screen and (width >= 768px) {
      margin-top: auto;
    }
  }

  .price_tag {
    font-size: var(--yb-text-base);
    font-weight: var(--yb-text-semi-bold);
    color: var(--yb-color-black);
  }

  &:hover {
    .title_text_clamp {
      color: var(--yb-color-primary);
    }
  }
}

.small-card {
  display: grid;
  max-width: 100%;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto;
  grid-auto-flow: row;
  grid-template-areas: 'image infos';
  gap: var(--yb-spacing-sm);
  height: var(--yb-small-card-height);
  min-height: var(--yb-small-card-height);

  picture {
    height: 100%;
    aspect-ratio: none;
  }

  img {
    height: 100%;
    object-fit: cover;
  }

  .infos_wrapper {
    overflow: hidden;
  }

  .favorite {
    top: 0.5rem;
    right: 0.5rem;
  }

  .close-button {
    position: absolute;
    background-color: var(--yb-color-white);
    border: none;
    top: var(--yb-spacing-xs);
    right: var(--yb-spacing-xs);
    z-index: 10;
  }

  .spacing_top {
    margin-top: 0px;
  }

  .title_text_clamp {
    font-size: var(--yb-text-base);
  }

  .small_text {
    font-size: var(--yb-text-xs);
  }
}

@media screen and (width < 768px) {
  .card_wrapper.row {
    display: grid;
    max-width: 100%;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: auto;
    grid-auto-flow: row;
    grid-template-areas: 'image infos';
    gap: var(--yb-spacing-sm);

    img {
      grid-area: image;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .favorite {
      top: 0.5rem;
      right: 0.5rem;
    }

    .spacing_top {
      margin-top: 0px;
    }

    .title_text_clamp {
      font-size: var(--yb-text-base);
    }

    .small_text {
      font-size: var(--yb-text-xs);
    }
  }
}
</style>
